const AMOUNT_EXPRESSION_PATTERN = /^[\d+\-*/().,\s$]+$/

/**
 * Parses a numeric amount or calculator-style expression.
 * Returns null when the input cannot be evaluated safely.
 */
export function evaluateAmountInput(rawInput: string): number | null {
  const cleanedInput = rawInput.trim()
  if (!cleanedInput) return null
  if (!AMOUNT_EXPRESSION_PATTERN.test(cleanedInput)) return null

  const normalized = cleanedInput.replace(/[$,\s]/g, "")
  if (!normalized) return null

  const parser = new ExpressionParser(normalized)
  const value = parser.parseExpression()
  if (!Number.isFinite(value) || !parser.isAtEnd()) {
    return null
  }

  return roundToCents(value)
}

function roundToCents(value: number): number {
  return Math.round(value * 100) / 100
}

class ExpressionParser {
  private readonly input: string
  private index = 0

  constructor(input: string) {
    this.input = input
  }

  parseExpression(): number {
    let value = this.parseTerm()
    while (this.match("+") || this.match("-")) {
      const operator = this.previous()
      const right = this.parseTerm()
      value = operator === "+" ? value + right : value - right
    }
    return value
  }

  isAtEnd(): boolean {
    return this.index >= this.input.length
  }

  private parseTerm(): number {
    let value = this.parseFactor()
    while (this.match("*") || this.match("/")) {
      const operator = this.previous()
      const right = this.parseFactor()
      if (operator === "/") {
        if (right === 0) throw new Error("Division by zero")
        value = value / right
      } else {
        value = value * right
      }
    }
    return value
  }

  private parseFactor(): number {
    if (this.match("+")) {
      return this.parseFactor()
    }
    if (this.match("-")) {
      return -this.parseFactor()
    }
    if (this.match("(")) {
      const value = this.parseExpression()
      if (!this.match(")")) throw new Error("Missing closing parenthesis")
      return value
    }
    return this.parseNumber()
  }

  private parseNumber(): number {
    const start = this.index
    let sawDot = false

    while (!this.isAtEnd()) {
      const ch = this.peek()
      if (ch >= "0" && ch <= "9") {
        this.index += 1
        continue
      }
      if (ch === "." && !sawDot) {
        sawDot = true
        this.index += 1
        continue
      }
      break
    }

    if (start === this.index) {
      throw new Error("Expected number")
    }

    const value = Number.parseFloat(this.input.slice(start, this.index))
    if (!Number.isFinite(value)) {
      throw new Error("Invalid number")
    }

    return value
  }

  private match(expected: string): boolean {
    if (this.isAtEnd() || this.input[this.index] !== expected) {
      return false
    }
    this.index += 1
    return true
  }

  private peek(): string {
    return this.input[this.index]
  }

  private previous(): string {
    return this.input[this.index - 1]
  }
}
